import React from 'react';
import styled from 'styled-components';
import {
  ShoppingCart as ShoppingCartIcon,
  QrCodeScanner as QrCodeScannerIcon,
  AddCard as AddCardIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import i18n from '../i18n/i18n';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import { useSession } from '../contexts/SessionContext';
import { useTheme } from '../contexts/ThemeContext';
import HelexiaQrIcon from '../images/helexia_qr_icon.svg';
import HelexiaAddCardIcon from '../images/helexia_add_card_icon.svg';

interface IconContainerProps {
  disabled?: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  color: rgba(77,76,77,255);
`;

const IconContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 24.5%;
`;

const IconDescription = styled.span<{ disabled?: boolean, textColor?: string, helexiaTheme?: boolean }>`
  padding: 4px;
  font-size: 13px;
  text-align: center;
  display: table;
  z-index: 2;
  color: ${(props) => (props.disabled ? (props.helexiaTheme ? 'rgb(121,118,121)' : 'rgba(255, 255, 255, 0.4)') : props.textColor || 'white')};
`;

const iconsData = [
  {
    name: 'shop', icon: ShoppingCartIcon, label: 'shop', disabled: false,
  },
  {
    name: 'qrCodeScanner', icon: QrCodeScannerIcon, label: 'read_qr', disabled: false,
  },
  {
    name: 'addCard', icon: AddCardIcon, label: 'add_card', disabled: false,
  },
];

interface ActionButtonsListProps {
  history: any;
  paymentMethod: any;
  menu: string;
  onError?: (message: string) => void;
}

const ActionButtons: React.FC<ActionButtonsListProps> = ({
  history,
  paymentMethod,
  menu,
  onError,
}) => {
  const filteredIconsData = menu === 'initialMenu'
    ? iconsData.filter(({ name }) => name !== 'shop')
    : iconsData.filter(({ name }) => name !== 'qrCodeScanner');
  const disabledIcons = ['addCard'];

  const { stickerId } = useVendingMachine();
  const { selectPaymentMethod, sessionError } = useSession();
  const { theme, themeName } = useTheme();

  const iconColor = theme.actionButtonIconColor || 'rgb(255, 255, 255)';
  const isHelexia = !!theme.actionButtonIconColor;
  const isGalp = themeName === 'galp';

  const galpLabelStyle: React.CSSProperties = {
    fontFamily: theme.bodyFont,
    fontSize: '9px',
    color: '#010101',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  };

  // Opens an SPG session in CARD mode, then navigates to the SIBS hosted-form
  // screen once the server returns the form context. Mirrors the EV card tap
  // in VMView so card payment works on non-EV machines via the shop action.
  const startCardPayment = async () => {
    try {
      const res = await selectPaymentMethod({
        gatewayName: 'SPG',
        gatewayParameters: { method: 'CARD' },
      });
      if (res?.spgCard) {
        history.push(`/spgcard?s=${stickerId}`, { spgCard: res.spgCard });
      } else if (onError) {
        onError(sessionError?.current || i18n.t('card_payment_error'));
      }
    } catch (e) {
      if (onError) {
        onError(i18n.t('card_payment_error'));
      }
    }
  };

  if (isGalp) {
    return (
      <div style={{
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => history.push('/readQr')}
          onKeyDown={(e) => e.key === 'Enter' && history.push('/readQr')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
          }}
        >
          <img src="/companies/GALP/qr_icon.svg" alt="qr" style={{ width: '46px', height: '46px' }} />
          <span style={galpLabelStyle}>{i18n.t('read_qr_code')}</span>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          opacity: 0.5,
          cursor: 'not-allowed',
        }}
        >
          <img src="/companies/GALP/add_card_icon.svg" alt="add card" style={{ width: '46px', height: '46px' }} />
          <span style={galpLabelStyle}>{i18n.t('add_card_short')}</span>
        </div>
      </div>
    );
  }

  const onClickHandler = async (name: string) => {
    switch (name) {
      case 'shop':
        // No payment method to pay with (e.g. the SPG gateway allows none of the methods Pay offers)
        if (!paymentMethod) {
          break;
        }
        if (paymentMethod.gatewayName === 'SPGCARD') {
          await startCardPayment();
        } else if (paymentMethod.gatewayName === 'SPG' || paymentMethod.gatewayName === 'MBWAY') {
          history.push(`/mbway?s=${stickerId}`, { paymentMethod });
        } else {
          selectPaymentMethod({
            gatewayName: 'TOKEN',
            gatewayParameters: { walletId: paymentMethod?.wallet?.id },
            maxCredit: paymentMethod?.wallet?.maxCredit,
          }, paymentMethod?.wallet?.balance);
          history.push(`/product-selection?s=${stickerId}`);
        }
        break;
      case 'qrCodeScanner':
        history.push('/readQr');
        break;
      /* case 'addCard':
        break; */
      default:
        break;
    }
  };

  return (
    <IconContainer>
      {filteredIconsData.map(({
        name, icon: IconComponent, label,
      }) => {
        const isIconDisabled = disabledIcons.includes(name);

        return (
          <IconContent key={name} style={isHelexia ? { width: '20%' } : {}}>
            <IconButton
              aria-label={label}
              size="large"
              style={{
                fontSize: isHelexia ? '2rem' : '1.5rem',
                width: isHelexia ? '40px' : undefined,
                height: isHelexia ? '40px' : undefined,
                backgroundColor: 'rgba(255,255,255,0.4)',
                color: isIconDisabled ? '' : iconColor,
              }}
              disabled={isIconDisabled}
              onClick={() => { onClickHandler(name); }}
            >
              {isHelexia && name === 'qrCodeScanner' ? (
                <img src={HelexiaQrIcon} alt="qr" style={{ width: '1.5rem', height: '1.5rem' }} />
              ) : isHelexia && name === 'addCard' ? (
                <img src={HelexiaAddCardIcon} alt="add card" style={{ width: '1.5rem', height: '1.5rem' }} />
              ) : (
                <IconComponent fontSize="inherit" />
              )}
            </IconButton>
            <IconDescription disabled={isIconDisabled} textColor={iconColor} helexiaTheme={isHelexia}>
              {i18n.t(label)}
            </IconDescription>
          </IconContent>
        );
      })}
    </IconContainer>
  );
};
export default ActionButtons;
